/**
 * useBudgetStatus Hook
 *
 * 예산 대비 지출 현황을 계산하는 React Hook
 * Budget 데이터와 Transaction 데이터를 결합하여 각 카테고리별 상태를 계산
 */

import { useMemo } from 'react'
import { useBudgets } from './useBudgets'
import { useTransactions } from '@/domains/transaction/hooks/useTransactions'
import { useCategories } from '@/domains/category/hooks/useCategories'
import { getMonthDateRange } from '@/shared/utils/dateUtils'
import type { BudgetStatus, MinorBudgetStatus, Budget } from '../types/Budget'
import type { Transaction } from '@/domains/transaction/types/Transaction'
import type { Category } from '@/domains/category/types/Category'

/**
 * useBudgetStatus Hook 반환 타입
 */
export interface UseBudgetStatusReturn {
  /** 카테고리별 예산 상태 배열 */
  budgetStatuses: BudgetStatus[]
  /** 전체 예산 합계 */
  totalBudget: number
  /** 전체 지출 합계 */
  totalSpent: number
  /** 전체 남은 예산 */
  totalRemaining: number
  /** 전체 사용 비율 (%) */
  totalPercentage: number
  /** 로딩 상태 (budgets 또는 transactions 로딩 중) */
  loading: boolean
  /** 에러 정보 */
  error: Error | null
}

/**
 * 예산 대비 지출 현황을 계산하는 Hook
 *
 * 책임:
 * - Budget 데이터 로딩 (useBudgets)
 * - Transaction 데이터 로딩 (useTransactions)
 * - 카테고리별 지출 합계 계산
 * - 예산 대비 비율, 초과 여부 계산
 * - 전체 합계 계산
 *
 * @param year 조회할 연도
 * @param month 조회할 월 (1-12)
 * @returns 예산 상태 데이터
 *
 * @example
 * ```typescript
 * const { budgetStatuses, totalBudget, totalSpent, loading } =
 *   useBudgetStatus(2025, 12)
 * ```
 */

/**
 * 소분류 예산 현황 계산 헬퍼 함수
 *
 * 책임:
 * - 대분류에 속한 모든 소분류 카테고리 조회
 * - 각 소분류의 예산 매칭 (있으면)
 * - 각 소분류의 지출 합계 계산
 * - 예산 유무에 따른 MinorBudgetStatus 생성
 *
 * @param majorBudget 대분류 예산 객체
 * @param budgets 전체 예산 목록 (소분류 예산 포함)
 * @param transactions 전체 거래 목록
 * @param majorCategory 대분류 카테고리 객체 (실제 구글 시트 데이터)
 * @returns 소분류 예산 현황 배열
 */
function calculateMinorBreakdown(
  majorBudget: Budget,
  budgets: Budget[],
  transactions: Transaction[],
  majorCategory: Category | undefined
): MinorBudgetStatus[] {
  /**
   * 1. 해당 대분류의 모든 소분류 카테고리 조회
   *
   * majorCategory가 없으면 빈 배열 반환 (데이터 무결성 보호)
   * 이는 카테고리가 삭제되었지만 예산이 남아있는 경우 발생할 수 있음
   */
  if (!majorCategory) {
    console.warn(`카테고리를 찾을 수 없음: ${majorBudget.categoryId}`)
    return []
  }

  /**
   * 활성화된 소분류만 필터링
   * children이 없으면 빈 배열 사용
   */
  const minorCategories = majorCategory.children?.filter(c => c.isActive) || []

  /**
   * 2. 해당 대분류에 속한 소분류 예산들 필터링
   *
   * Note: Cascade delete 구현으로 고아 예산은 발생하지 않아야 함.
   * 만약 parentBudgetId가 비활성 예산을 가리킨다면 데이터 무결성 에러 상태이며,
   * window.debugBudgets()를 실행하여 정리할 수 있음.
   */
  const minorBudgets = budgets.filter(b => b.parentBudgetId === majorBudget.id)

  /**
   * 3. 각 소분류 카테고리별 상태 계산
   */
  return minorCategories.map(minorCategory => {
    /**
     * 3-1. 해당 소분류의 지출 합계 계산
     * minorCategoryId로 거래 필터링
     */
    const spent = transactions
      .filter(tx => tx.minorCategoryId === minorCategory.id)
      .reduce((sum, tx) => sum + tx.amount, 0)

    /**
     * 3-2. 해당 소분류의 예산 찾기 (있으면)
     */
    const budget = minorBudgets.find(b => b.categoryId === minorCategory.id)

    /**
     * 3-3. 예산이 없는 경우: "예산 미지정" 상태 반환
     */
    if (!budget) {
      return {
        minorCategoryId: minorCategory.id,
        budget: null,
        spent,
        remaining: 0,
        percentage: 0,
        isOverBudget: false,
        hasNoBudget: true,
      }
    }

    /**
     * 3-4. 예산이 있는 경우: 예산 대비 상태 계산
     */
    const remaining = budget.amount - spent
    const percentage = budget.amount > 0 ? (spent / budget.amount) * 100 : 0
    const isOverBudget = spent > budget.amount

    return {
      minorCategoryId: minorCategory.id,
      budget,
      spent,
      remaining,
      percentage,
      isOverBudget,
      hasNoBudget: false,
    }
  })
}

export function useBudgetStatus(year: number, month: number): UseBudgetStatusReturn {
  /**
   * 예산 데이터 로딩 (템플릿 + 특정 월 우선순위 적용)
   * - year/month에 맞는 예산 조회
   * - 특정 월 예산이 있으면 우선 사용, 없으면 템플릿 사용
   */
  const { budgets, loading: budgetsLoading, error: budgetsError } = useBudgets({
    year,
    month,       // month 추가: Repository 우선순위 로직 활성화
    isActive: true,
  })

  /**
   * 해당 월의 지출 거래 데이터 로딩
   * startDate ~ endDate 범위로 필터링
   *
   * 날짜 범위 계산: getMonthDateRange 유틸리티 사용
   * - 각 월의 실제 마지막 날 계산 (윤년 고려)
   * - 예: 2월은 28일 또는 29일, 4/6/9/11월은 30일
   */
  const [startDate, endDate] = getMonthDateRange(year, month)

  const { transactions, loading: transactionsLoading, error: transactionsError } = useTransactions({
    startDate,
    endDate,
    type: 'expense', // 지출만 조회
  })

  /**
   * 카테고리 데이터 로딩
   * Category-driven 접근을 위해 필요
   * - 예산이 없어도 지출이 있는 카테고리를 표시하기 위함
   */
  const { expenseCategories, loading: categoriesLoading } = useCategories()

  /**
   * 대분류 예산만 필터링
   * 소분류 예산은 minorBreakdown에서 처리
   */
  const majorBudgets = useMemo(() => {
    return budgets.filter(b => !b.parentBudgetId)
  }, [budgets])

  /**
   * 카테고리별 예산 상태 계산 (개선 버전: Category-driven + Budget)
   *
   * 변경 사항:
   * - 기존: 예산이 있는 카테고리만 표시 (Budget-driven)
   * - 개선: 예산 또는 지출이 있는 카테고리 표시 (Category-driven + Budget)
   *
   * 로직:
   * 1. 모든 지출 카테고리 순회
   * 2. 각 카테고리의 예산 찾기 (있으면)
   * 3. 각 카테고리의 지출 계산
   * 4. 예산 또는 지출이 0보다 크면 BudgetStatus 생성
   *
   * 결과: 사용자가 예산을 설정하지 않아도 지출이 있으면 현황에 표시됨
   */
  const budgetStatuses = useMemo<BudgetStatus[]>(() => {
    // 1. 예산 맵 생성 (categoryId -> Budget)
    const budgetMap = new Map<string, Budget>()
    budgets.forEach(budget => {
      if (!budget.parentBudgetId) {
        budgetMap.set(budget.categoryId, budget)
      }
    })

    /**
     * 2. 카테고리 맵 생성 (categoryId -> Category)
     * 성능 최적화: O(n) find() 대신 O(1) Map.get() 사용
     */
    const categoryMap = new Map<string, Category>()
    expenseCategories.forEach(category => {
      categoryMap.set(category.id, category)
    })

    // 3. 카테고리별 지출 계산 (majorCategoryId -> total spent)
    const spentByCategory = new Map<string, number>()
    transactions.forEach(tx => {
      const current = spentByCategory.get(tx.majorCategoryId) || 0
      spentByCategory.set(tx.majorCategoryId, current + tx.amount)
    })

    // 4. 모든 지출 카테고리 순회하여 BudgetStatus 생성
    const statuses: BudgetStatus[] = []

    expenseCategories.forEach(category => {
      const categoryId = category.id
      const budget = budgetMap.get(categoryId)
      const spent = spentByCategory.get(categoryId) || 0

      // 예산도 없고 지출도 없으면 스킵
      if (!budget && spent === 0) {
        return
      }

      // 예산이 없는 경우: 가상 Budget 객체 생성
      if (!budget) {
        const virtualBudget: Budget = {
          id: `virtual-${categoryId}`,
          categoryId,
          amount: 0,
          year: null,
          month: null,
          parentBudgetId: undefined,
          isActive: true,
        }

        const majorCategory = categoryMap.get(categoryId)

        statuses.push({
          budget: virtualBudget,
          spent,
          remaining: -spent, // 음수 (예산 초과)
          percentage: 0, // 예산이 0이므로 비율 의미 없음
          isOverBudget: spent > 0,
          minorBreakdown: calculateMinorBreakdown(virtualBudget, budgets, transactions, majorCategory),
          hasNoBudget: true, // 예산 미설정 플래그
        })
        return
      }

      // 예산이 있는 경우: 기존 로직
      const remaining = budget.amount - spent
      const percentage = budget.amount > 0 ? (spent / budget.amount) * 100 : 0
      const isOverBudget = spent > budget.amount

      const majorCategory = categoryMap.get(categoryId)

      statuses.push({
        budget,
        spent,
        remaining,
        percentage,
        isOverBudget,
        minorBreakdown: calculateMinorBreakdown(budget, budgets, transactions, majorCategory),
        hasNoBudget: false,
      })
    })

    return statuses
  }, [expenseCategories, budgets, transactions])

  /**
   * 전체 예산 합계
   *
   * 주의: 대분류 예산만 합산 (소분류는 대분류에 포함되므로 제외)
   * 이유: 소분류 예산은 대분류 예산을 세분화한 것이므로 중복 계산 방지
   *
   * 예시:
   * - 식비(대분류): 600,000원
   *   - 식사(소분류): 300,000원
   *   - 카페(소분류): 200,000원
   * → 총 예산: 600,000원 (대분류만)
   */
  const totalBudget = useMemo(() => {
    return majorBudgets.reduce((sum, budget) => sum + budget.amount, 0)
  }, [majorBudgets])

  /**
   * 전체 지출 합계
   */
  const totalSpent = useMemo(() => {
    return budgetStatuses.reduce((sum, status) => sum + status.spent, 0)
  }, [budgetStatuses])

  /**
   * 전체 남은 예산
   */
  const totalRemaining = totalBudget - totalSpent

  /**
   * 전체 사용 비율 (0으로 나누기 방지)
   */
  const totalPercentage = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0

  /**
   * 로딩 상태: budgets, transactions, categories 중 하나라도 로딩 중이면 true
   */
  const loading = budgetsLoading || transactionsLoading || categoriesLoading

  /**
   * 에러 처리: budgets 에러를 우선으로 반환
   */
  const error = budgetsError || transactionsError

  return {
    budgetStatuses,
    totalBudget,
    totalSpent,
    totalRemaining,
    totalPercentage,
    loading,
    error,
  }
}
